import { Injectable } from '@angular/core';
import { GlobalConstants } from '../base/global.constants';
import { RegexPatternConstants } from '../base/regex-pattern.constants';

declare const monaco: any;

// Cache for language registration
const registeredLanguages = new Set<string>();

// Cache for registered completion providers per language to prevent duplicates
const registeredCompletionProviders = new Map<string, { disposable: any; callbacks: Set<() => any[]> }>();

@Injectable({
  providedIn: 'root'
})
export class MonacoEditorHelperService {
  // Use regex patterns from GlobalConstants
  private readonly chipPattern = GlobalConstants.queryStringRegex.chipPattern;
  private readonly chipPatternNonEmpty = GlobalConstants.queryStringRegex.chipPatternNonEmpty;

  constructor() {
    // Initialization handled by injected dependencies
  }

  registerCustomLanguage(languageId: string) {
    // Use cached set for faster lookup
    if (registeredLanguages.has(languageId)) {
      return;
    }

    // Double-check with Monaco API
    const languages = monaco.languages.getLanguages();
    const languageExists = languages.some((lang: any) => lang.id === languageId);

    if (!languageExists) {
      monaco.languages.register({ id: languageId });

      monaco.languages.setMonarchTokensProvider(languageId, {
        tokenizer: {
          root: [
            // Match $${...} as a single token (must come before other rules)
            [GlobalConstants.queryStringRegex.monarchTokenizerPattern, 'variable.querystring']
          ]
        }
      });

      monaco.languages.setLanguageConfiguration(languageId, {
        brackets: [
          ['{', '}'],
          ['[', ']'],
          ['(', ')']
        ],
        autoClosingPairs: [
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' }
        ]
      });
    }

    registeredLanguages.add(languageId);
  }

  /**
   * Get or register a completion provider for a language.
   * Only one provider is registered per language to prevent duplicate suggestions.
   * Multiple callbacks can be registered and will be merged when providing completions.
   * Returns a disposable that removes the callback (not the provider itself).
   */
  getCompletionProvider(languageId: string, getQueryStringsCallback: () => any[], enableUserVariables = false) {
    // Check if provider already exists for this language
    const existingProvider = registeredCompletionProviders.get(languageId);

    if (existingProvider) {
      // Provider already registered, just add this callback to the set
      existingProvider.callbacks.add(getQueryStringsCallback);

      // Return a disposable that removes this specific callback
      return {
        dispose: () => {
          existingProvider.callbacks.delete(getQueryStringsCallback);
        }
      };
    }

    // First time registration for this language
    const callbacks = new Set<() => any[]>();
    callbacks.add(getQueryStringsCallback);

    const triggerCharacters = enableUserVariables ? [':', '$'] : ['$'];
    const disposable = monaco.languages.registerCompletionItemProvider(languageId, {
      triggerCharacters: triggerCharacters,
      provideCompletionItems: (model: any, position: any) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });

        const users = [
          'CurrentUser_FirstName',
          'CurrentUser_LastName',
          'CurrentUser_Email',
          'CurrentUser_LanguageCode'
        ];

        const suggestions: any[] = [];

        // Case 1: User variables trigger (:)
        if (enableUserVariables && textUntilPosition.endsWith(':')) {
          users.forEach((user: any) => {
            suggestions.push({
              label: `${user}`,
              kind: monaco.languages.CompletionItemKind.Variable,
              insertText: `${user}`,
              detail: 'User variable',
              documentation: 'Current user information'
            });
          });
          return { suggestions: suggestions };
        }

        // Case 2: Query Strings trigger ($ or $$ or $${ or $${word)
        const dollarMatch = textUntilPosition.match(RegexPatternConstants.dollarMatch);
        if (dollarMatch) {
          const fullMatch = dollarMatch[0]; // The entire matched prefix (e.g., "$$", "$${", "$${Get")
          const typedText = dollarMatch[3] || ''; // The text typed after $${ (e.g., "Get", "find")

          // Get the current callbacks from the cache (in case they were updated)
          const currentProvider = registeredCompletionProviders.get(languageId);
          const activeCallbacks = currentProvider ? currentProvider.callbacks : callbacks;

          // Use the first available callback to get query strings
          // (all callbacks should return the same data from appGlobalService)
          let queryStrings: any = {};
          for (const cb of activeCallbacks) {
            queryStrings = cb();
            if (queryStrings && Object.keys(queryStrings).length > 0) {
              break;
            }
          }

          // Calculate the range to replace
          const startColumn = position.column - fullMatch.length;
          const replaceRange = new monaco.Range(position.lineNumber, startColumn, position.lineNumber, position.column);

          if (queryStrings && Object.values(queryStrings)?.length > 0) {
            Object.values(queryStrings).forEach((queryString: any) => {
              if (queryString) {
                // Filter suggestions based on what user has typed
                const queryStringLower = queryString.toLowerCase();
                const typedTextLower = typedText.toLowerCase();

                // Only include suggestions that match the typed text
                if (
                  !typedText ||
                  queryStringLower.includes(typedTextLower) ||
                  typedTextLower.includes(queryStringLower.substring(0, typedTextLower.length))
                ) {
                  suggestions.push({
                    label: `${queryString}`,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    insertText: '$${' + queryString + '}',
                    range: replaceRange,
                    filterText: '$${' + queryString + '}',
                    sortText: `0-${queryString}`
                  });
                }
              }
            });
          }
        }

        return { suggestions: suggestions };
      }
    });

    // Store in cache
    registeredCompletionProviders.set(languageId, { disposable, callbacks });

    // Return a disposable that removes this specific callback
    return {
      dispose: () => {
        const provider = registeredCompletionProviders.get(languageId);
        if (provider) {
          provider.callbacks.delete(getQueryStringsCallback);
        }
      }
    };
  }

  getPreviewEditorOptions() {
    return {
      language: 'sql',
      readOnly: true,
      wordWrap: 'on'
    };
  }

  setupHighlighting(editor: any, getAllowedTags: () => string[]) {
    let decorationIds: string[] = [];

    const updateDecorations = () => {
      decorationIds = this.highlightAndRemoveInvalidTags(editor, getAllowedTags(), decorationIds);
    };

    // Initial
    setTimeout(updateDecorations, 100);

    // On Change
    const changeDisposable = editor.onDidChangeModelContent(updateDecorations);

    // Atomic Chips
    const atomicDisposables = this.setupAtomicChips(editor);

    return {
      dispose: () => {
        changeDisposable.dispose();
        atomicDisposables.forEach((d) => d.dispose());
      }
    };
  }

  setupAtomicChips(editor: any): any[] {
    const disposables: any[] = [];
    let lastPosition: any = null;

    // 1. Cursor Guard - Handle cursor position changes to skip over chips
    disposables.push(
      editor.onDidChangeCursorPosition((e: any) => {
        const position = e.position;
        const model = editor.getModel();
        if (!model) {
          return;
        }

        const matches = model.findMatches(
          GlobalConstants.queryStringRegex.monacoChipPattern,
          true,
          true,
          false,
          null,
          true
        );

        for (const match of matches) {
          const range = match.range;
          if (range.startLineNumber === range.endLineNumber) {
            if (position.lineNumber === range.startLineNumber) {
              // Check if cursor is inside the chip (not at boundaries)
              if (position.column > range.startColumn && position.column < range.endColumn) {
                let newColumn: number;

                // Determine direction based on previous position
                if (lastPosition && lastPosition.lineNumber === position.lineNumber) {
                  if (lastPosition.column <= range.startColumn) {
                    // Moving right - skip to end of chip
                    newColumn = range.endColumn;
                  } else if (lastPosition.column >= range.endColumn) {
                    // Moving left - skip to start of chip
                    newColumn = range.startColumn;
                  } else {
                    // Default: move to nearest edge
                    const distToStart = position.column - range.startColumn;
                    const distToEnd = range.endColumn - position.column;
                    newColumn = distToStart < distToEnd ? range.startColumn : range.endColumn;
                  }
                } else {
                  // No previous position or different line - move to nearest edge
                  const distToStart = position.column - range.startColumn;
                  const distToEnd = range.endColumn - position.column;
                  newColumn = distToStart < distToEnd ? range.startColumn : range.endColumn;
                }

                editor.setPosition({ lineNumber: position.lineNumber, column: newColumn });
                lastPosition = { lineNumber: position.lineNumber, column: newColumn };
                return;
              }
            }
          }
        }

        // Update last position
        lastPosition = { lineNumber: position.lineNumber, column: position.column };
      })
    );

    // 2. Deletion Guard
    disposables.push(
      editor.onKeyDown((e: any) => {
        const selection = editor.getSelection();
        if (!selection.isEmpty()) {
          return;
        }

        const position = selection.getPosition();
        const model = editor.getModel();
        if (!model) {
          return;
        }

        // Backspace = 1, Delete = 20
        if (e.keyCode === 1 || e.keyCode === 20) {
          const matches = model.findMatches(
            GlobalConstants.queryStringRegex.monacoChipPattern,
            true,
            true,
            false,
            null,
            true
          );

          for (const match of matches) {
            const range = match.range;

            // Backspace at end of chip
            if (e.keyCode === 1 && range.endLineNumber === position.lineNumber && range.endColumn === position.column) {
              editor.executeEdits('delete-chip', [{ range: range, text: '', forceMoveMarkers: true }]);
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            // Delete at start of chip
            if (
              e.keyCode === 20 &&
              range.startLineNumber === position.lineNumber &&
              range.startColumn === position.column
            ) {
              editor.executeEdits('delete-chip', [{ range: range, text: '', forceMoveMarkers: true }]);
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }
      })
    );

    return disposables;
  }

  // Version with removal if needed
  highlightAndRemoveInvalidTags(editorInstance: any, allowedTags: string[], decorationIds: string[]): string[] {
    if (!editorInstance) {
      return decorationIds;
    }

    const model = editorInstance.getModel();
    if (!model) {
      return decorationIds;
    }

    const matches = model.findMatches(
      GlobalConstants.queryStringRegex.monacoChipPattern,
      true,
      true,
      false,
      null,
      true
    );

    const validDecorations: any[] = [];
    const invalidRanges: any[] = [];

    matches.forEach((match: any) => {
      const text = model.getValueInRange(match.range);
      // Create a new regex instance to avoid lastIndex issues
      const tagMatch = text.match(new RegExp(GlobalConstants.queryStringRegex.monacoChipPattern));

      if (tagMatch) {
        const tag = tagMatch[1];

        // Skip empty tags - don't highlight but don't remove
        if (!tag || tag.length === 0) {
          return;
        }

        const exists = allowedTags.includes(tag);
        if (exists) {
          // Valid query string (including those with spaces) - highlight
          validDecorations.push({
            range: match.range,
            options: { inlineClassName: 'querystring-highlight p-chip' }
          });
        } else if (!GlobalConstants.queryStringRegex.whitespacePattern.test(tag)) {
          // Tag looks like a query string reference (no spaces) but doesn't exist - remove it
          // Tags with spaces that don't exist are left alone (not removed)
          invalidRanges.push(match.range);
        }
        // Tags with spaces that don't exist in allowedTags are left as-is (no highlight, no removal)
      }
      // If tagMatch fails, it's not a complete $${...} pattern - leave it alone
    });

    const newDecorationIds = editorInstance.deltaDecorations(decorationIds, validDecorations);

    if (invalidRanges.length > 0) {
      editorInstance.executeEdits(
        'remove-invalid-tags',
        invalidRanges.map((range: any) => ({
          range,
          text: '',
          forceMoveMarkers: true
        }))
      );
    }

    return newDecorationIds;
  }

  getQueryStringParts(content: string): { type: 'text' | 'chip'; value: string }[] {
    if (!content) {
      return [];
    }

    const parts: { type: 'text' | 'chip'; value: string }[] = [];
    // Use a new regex instance to avoid lastIndex issues
    const regex = new RegExp(this.chipPatternNonEmpty.source, 'g');
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', value: content.substring(lastIndex, match.index) });
      }
      parts.push({ type: 'chip', value: match[0] });
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < content.length) {
      parts.push({ type: 'text', value: content.substring(lastIndex) });
    }

    return parts;
  }

  /**
   * Convert query string content from names to SIDs for storage
   * Replaces $${name} with $${sid} using the provided maps
   * @param query The query string containing $${name} references
   * @param queryStringsMap Map of sid -> name from appGlobalService.get('queryStrings')
   * @param appMetaInfo Map of sid -> name from appGlobalService.get('appmetaInfo')
   * @returns Query with names replaced by SIDs in $${sid} format
   */
  formatQSNameToSid(
    query: string,
    queryStringsMap: Record<string, string> = {},
    appMetaInfo: Record<string, string> = {}
  ): string {
    if (!query) {
      return query;
    }

    // Build reverse map: name -> sid
    const nameToSidMap: Record<string, string> = {};

    // From queryStrings map (sid -> name)
    Object.keys(queryStringsMap).forEach((sid) => {
      const name = queryStringsMap[sid];
      if (name) {
        nameToSidMap[name] = sid;
      }
    });

    // From appMetaInfo map (sid -> name) - only for querystrings that might not be in queryStringsMap
    Object.keys(appMetaInfo).forEach((sid) => {
      const name = appMetaInfo[sid];
      if (name && !nameToSidMap[name]) {
        nameToSidMap[name] = sid;
      }
    });

    // Replace $${name} with $${sid}
    return query.replace(
      new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g'),
      (match, name) => {
        const sid = nameToSidMap[name];
        return sid ? '$${' + sid + '}' : match;
      }
    );
  }

  /**
   * Convert query string content from SIDs to names for display
   * Replaces $${sid} with $${name} using the provided map
   * @param query The query string containing $${sid} references
   * @param appMetaInfo Map of sid -> name from appGlobalService.get('appmetaInfo')
   * @returns Query with SIDs replaced by names in $${name} format
   */
  formatQSSidToName(query: string, appMetaInfo: Record<string, string> = {}): string {
    if (!query) {
      return query;
    }

    // Replace $${sid} with $${name}
    return query.replace(new RegExp(GlobalConstants.queryStringRegex.chipPatternNonEmpty.source, 'g'), (match, sid) => {
      const name = appMetaInfo && appMetaInfo[sid];
      return name ? '$${' + name + '}' : match;
    });
  }
}
